import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { declineReasons, unHireReasons } from '../../common/general-types/filters.dto';
import { FilterItemDto } from '../../common/app-config/filter-models/misc';
import { ApplicationStatusEnum } from '../../common/general-types/briefs.dto';
import { BriefApplicationResponseDto } from '../../common/general-types/briefs.dto';
import { FormsModule } from '@angular/forms';
import { FormField, form, minLength, required } from '@angular/forms/signals';
import { BriefApplicationService } from '../../services/brief-application/brief-application.service';
import { DeclineBriefApplicationDto } from '../../common/general-types/brief-application.dto';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { canUnhireApplication } from '../../pages/admin-options/settings/molecules/brief-tracker/brief-tracker.helpers';
import { isShoutAgencyAdmin as hasShoutAgencyAdminRole } from '../../common/support-admin-roles.util';
import { AuthService } from '../../services/auth/auth.service';

@Component({
  selector: 'atom-decline-application-drawer',
  standalone: true,
  imports: [FormsModule, FormField],
  templateUrl: './decline-application-drawer.component.html',
  styleUrl: './decline-application-drawer.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DeclineApplicationDrawerComponent implements OnInit, OnDestroy {

  ApplicationStatusEnum = ApplicationStatusEnum;
  readonly canUnhireApplication = canUnhireApplication;

  @ViewChild('declineApplicationDrawerDialog', { static: false }) declineApplicationDrawerDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('declineApplicationDrawerButton', { static: false }) declineApplicationDrawerButton?: ElementRef<HTMLButtonElement>;

  @Input() applicationStatus: ApplicationStatusEnum = ApplicationStatusEnum.Applied;
  @Input() selectedApplication!: BriefApplicationResponseDto;
  @Output() close = new EventEmitter<void>();

  declineReasons: FilterItemDto[] = declineReasons;
  unHireReasons: FilterItemDto[] = unHireReasons;

  selectedReason = signal<FilterItemDto | null>(null);
  otherReason = signal<string | null>(null);

  /** Services */
  private briefApplicationService = inject(BriefApplicationService);
  private firebaseService = inject(FirebaseService);
  private readonly authService = inject(AuthService);
  /** Services - END */



  /** Forms */
  otherReasonsModel = signal({
    otherReason: '',
  });
  otherReasonsForm = form(this.otherReasonsModel, (schemaPath) => {
    required(schemaPath.otherReason, { message: 'Other reason is required' });
    minLength(schemaPath.otherReason, 10, { message: 'Other reason must be at least 10 characters long' });
  });

  // Sync otherReasonsModel().otherReason to otherReason signal
  private syncOtherReason = effect(() => {
    const value = this.otherReasonsModel().otherReason;
    this.otherReason.set(value || null);
  });

  // Check if submit button should be disabled
  isSubmitDisabled = computed(() => {
    const reason = this.selectedReason();

    // Disabled if no reason selected
    if (!reason) {
      return true;
    }

    // If "Other" is selected (id === 8), check if form is invalid
    if (reason.id === 8) {
      if (this.otherReasonsModel().otherReason.length < 10) {
        return true;
      }
      return false;
    }

    return false;
  });

  private dialogCloseHandler?: () => void;

  get dialogId(): string {
    return `declineApplicationDrawer-${this.selectedApplication.id}`;
  }

  resetFormOnOpen(): void {
    this.selectedReason.set(null);
    this.otherReason.set(null);
    this.otherReasonsModel.set({ otherReason: '' });
  }

  get isShoutAgencyAdmin(): boolean {
    return hasShoutAgencyAdminRole(this.authService.userRoles);
  }

  get isUnHireFlow(): boolean {
    return canUnhireApplication(this.applicationStatus, this.isShoutAgencyAdmin);
  }

  ngOnInit(): void {
    console.log('DeclineApplicationDrawerComponent ngOnInit');
    console.log('Application Status: ', this.applicationStatus);
    console.log('Applicant: ', this.selectedApplication);
    if (this.applicationStatus === ApplicationStatusEnum.Applied) {
      this.declineReasons = this.declineReasons;
    } else if (this.isUnHireFlow) {
      this.unHireReasons = this.unHireReasons;
    }
  }

  onDialogKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      return;
    }
    event.stopPropagation();
  }

  selectReason(selectedReason: FilterItemDto) {
    if (selectedReason.id !== 8) {
      this.otherReason.set('');
    }
    this.selectedReason.set(selectedReason);
  }

  submit() {
    console.log('submit()');

    console.log('Selected application for Declining: ', this.selectedApplication);

    if (this.applicationStatus === ApplicationStatusEnum.Applied) {
      console.log('Submit() - Declining application');
      this.declineApplication();
    } else if (this.isUnHireFlow) {
      console.log('Submit() - Unhiring creator');
      this.unHireApplication();
    }
  }

  declineApplication() {
    let reason: string = '';

    if (this.selectedReason && this.selectedApplication) {
      if (this.selectedReason()?.id === 8) {
        if (this.otherReason && this.otherReason.length > 0) {
          reason = this.otherReason() || '';
        } else {
          reason = 'Other reason not provided'; // fallback message
        }
      } else {
        reason = this.selectedReason()?.name || '';
      }

      const setStatus: DeclineBriefApplicationDto = {
        application: this.selectedApplication,
        status: ApplicationStatusEnum.ApplicationDeclined,
        user: this.firebaseService.userId || '',
        reason: reason
      }

      this.briefApplicationService.declineApplicationSignal.set(setStatus);
      this.closeDrawer();
    }
  }

  async unHireApplication() {
    console.log('unHireApplication');

    let reason: string = '';

    if (this.selectedReason && this.selectedApplication) {
      if (this.selectedReason()?.id === 8) {
        if (this.otherReason && this.otherReason.length > 0) {
          reason = this.otherReason() || '';
        } else {
          reason = 'Other reason not provided'; // fallback message
        }
      } else {
        reason = this.selectedReason()?.name || '';
      }

      const setStatus: DeclineBriefApplicationDto = {
        application: this.selectedApplication,
        status: ApplicationStatusEnum.HiredRemoved,
        user: this.firebaseService.userId || '',
        reason: reason
      }

      this.briefApplicationService.unHireApplicationSignal.set(setStatus);
      this.closeDrawer();
    }
  }

  closeDrawer() {
    this.close.emit();
    const dialog = this.declineApplicationDrawerDialog?.nativeElement;
    if (dialog) {
      if (this.dialogCloseHandler) {
        dialog.removeEventListener('close', this.dialogCloseHandler);
      }
      dialog.close();
    }
  }

  ngOnDestroy(): void {
    console.log('DeclineApplicationDrawerComponent ngOnDestroy');
    this.selectedReason.set(null);
    this.otherReason.set(null);
  }
}
